import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { Profile, ProfileUpdate } from '@/types/database';

export class ProfilesService {
  private static readonly TABLE = 'profiles';
  private static readonly logger = logger.child({ service: 'ProfilesService' });
  /**
   * Get user profile by ID
   */
  static async getById(userId: string): Promise<Profile | null> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching profile', { userId });

      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Profile fetched', { userId, found: !!data });
      return data;
    }, { operation: 'getById', table: this.TABLE });
  }

  /**
   * Update user profile
   */
  static async update(userId: string, updates: ProfileUpdate): Promise<Profile | null> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Updating profile', { userId });

      const { data, error } = await supabase
        .from(this.TABLE)
        .update(updates)
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Profile updated', { userId });
      return data;
    }, { operation: 'update', table: this.TABLE });
  }

  /**
   * Get user's organization and role
   */
  static async getUserOrganization(userId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching user organization', { userId });

      const { data, error } = await supabase
        .from('user_roles')
        .select('organization_id, role, organizations(*)')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('User organization fetched', { userId, found: !!data });
      return data;
    }, { operation: 'getUserOrganization' });
  }

  /**
   * Get user's role in organization
   */
  static async getUserRole(userId: string, organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching user role', { userId, organizationId });

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('User role fetched', { userId, organizationId, role: data?.role });
      return data;
    }, { operation: 'getUserRole' });
  }
}
